import { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  defaultLocale,
  getLocalePath,
  isSupportedLocale,
  supportedLocales,
} from "@/i18n/routing";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
}

export function SEO({
  title,
  description,
  canonical,
  ogImage = "https://www.4sistersevents.ee/og-image.jpg",
  noindex = false
}: SEOProps) {
  const { t, i18n } = useTranslation();
  const resolvedLanguage = i18n.resolvedLanguage ?? "";
  const language = isSupportedLocale(resolvedLanguage)
    ? resolvedLanguage
    : defaultLocale;
  const resolvedCanonical = canonical ?? `https://www.4sistersevents.ee${getLocalePath(language)}`;
  const resolvedTitle = title ?? t("seo.title");
  const resolvedDescription = description ?? t("seo.description");

  useEffect(() => {
    const setMeta = (selector: string, value: string) => {
      document.querySelector<HTMLMetaElement>(selector)?.setAttribute("content", value);
    };

    const ensureAlternate = (languageCode: string, href: string) => {
      let link = document.querySelector<HTMLLinkElement>(
        `link[rel="alternate"][hreflang="${languageCode}"]`,
      );
      if (!link) {
        link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = languageCode;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    document.documentElement.lang = language;
    document.title = resolvedTitle;
    setMeta('meta[name="description"]', resolvedDescription);
    setMeta('meta[property="og:title"]', resolvedTitle);
    setMeta('meta[property="og:description"]', resolvedDescription);
    setMeta('meta[property="og:url"]', resolvedCanonical);
    setMeta('meta[property="og:image"]', ogImage);
    setMeta('meta[name="twitter:title"]', resolvedTitle);
    setMeta('meta[name="twitter:description"]', resolvedDescription);
    setMeta('meta[name="twitter:image"]', ogImage);

    document
      .querySelector<HTMLLinkElement>('link[rel="canonical"]')
      ?.setAttribute("href", resolvedCanonical);

    supportedLocales.forEach((locale) => {
      ensureAlternate(locale, `https://www.4sistersevents.ee${getLocalePath(locale)}`);
    });
    ensureAlternate("x-default", `https://www.4sistersevents.ee${getLocalePath(defaultLocale)}`);

    if (noindex) {
      setMeta('meta[name="robots"]', "noindex,nofollow");
    }
  }, [language, noindex, ogImage, resolvedCanonical, resolvedDescription, resolvedTitle]);

  return null;
}
